import { FC, PropsWithChildren, ReactNode, useRef, useState } from 'react'
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import styled from '@emotion/styled'
import { theme } from '../../theme/theme'

export const Tooltip: FC<
  PropsWithChildren<{
    tooltip: ReactNode
    disabled?: boolean
  }>
> = ({ children, tooltip, disabled }) => {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)
  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(8),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
    open: disabled ? false : open,
    onOpenChange: setOpen,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context),
    useClick(context, {
      ignoreMouse: true, // useHover for desktop, useClick for mobile
    }),
    useDismiss(context),
    useRole(context, { role: 'tooltip' }),
  ])

  return (
    <>
      <RefElement ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </RefElement>

      {context.open && (
        <FloatingPortal>
          <TooltipBubble ref={refs.setFloating} {...getFloatingProps()} style={floatingStyles}>
            {tooltip}

            <FloatingArrow ref={arrowRef} context={context} color={theme.colors.primary.main} />
          </TooltipBubble>
        </FloatingPortal>
      )}
    </>
  )
}

const RefElement = styled.div`
  display: inline-flex;
`

const TooltipBubble = styled.div`
  position: absolute;
  z-index: 9999;
  padding: 6px 10px;
  max-width: 240px;
  border-radius: 0.5em;
  box-shadow: ${({ theme }) => theme.shadows.box};
  background-color: ${({ theme }) => theme.colors.primary.main};
  font-size: 12px;
  color: white;
  pointer-events: none;
`
